import React, {useEffect, useRef, useState} from "react";
import SidebarFirstLevelMenuItem from "./SidebarFirstLevelMenuItem";
import SidebarDropDownFirstLevelMenuItem from "./SidebarDropDownFirstLevelMenuItem";
import axios from "axios";
import {toast} from "react-toastify";
import {useNavigate} from "react-router-dom";
import {jwtDecode} from "jwt-decode";
import {privateApi} from "../api";

type JwtPayload = {
    sub?: string;
    exp?: number;
    iat?: number;
    iss?: string;
    type?: string;
    username?: string;
}

type PasswordCriteria = {
    regex: string;
    description: string;
};


function Sidebar() {
    const focus = localStorage.getItem("focus")

    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [showPasswordsActual, setShowPasswordsActual] = useState(false);
    const [showPasswordsNew, setShowPasswordsNew] = useState(false);
    const [criteriaList, setCriteriaList] = useState<PasswordCriteria[]>([]);
    const [loadingCriteria, setLoadingCriteria] = useState(false);

    const [inventarioOpen, setInventarioOpen] = useState<boolean>(focus === "brand" || focus === "dispositivi" || focus === "ricambi");
    const [configOpen, setConfigOpen] = useState<boolean>(focus === "stati" || focus === "lavorazioni");
    const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
    const [changePasswordModal, setChangePasswordModal] = useState<boolean>(false);

    const menuRef = useRef<HTMLDivElement | null>(null);
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const navigate = useNavigate();

    const accessToken: string = localStorage.getItem("accessToken") as string;
    let decodedJwt;
    let username;
    if (accessToken) {
        decodedJwt = jwtDecode<JwtPayload>(accessToken)
        username = decodedJwt.username;
    }

    const resetChangePasswordModal = () => {
        setOldPassword("");
        setNewPassword("");
    }

    // Close user menu when clicking outside
    const handleSubmit = async () => {
        try {
            await privateApi.put("/cr-order-manager/v1/user/password", {
                "oldPassword": oldPassword,
                "newPassword": newPassword
            })
            setChangePasswordModal(false);
            toast.success("Password cambiata con successo");
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        } finally {
            resetChangePasswordModal();
        }
    }

    const validateCriteria = (regex: string) => {
        try {
            return new RegExp(regex).test(newPassword);
        } catch {
            return false;
        }
    };

    useEffect(() => {
        function handleClick(e: MouseEvent) {
            const target = e.target as Node;

            if (
                menuRef.current &&
                btnRef.current &&
                !menuRef.current.contains(target) &&
                !btnRef.current.contains(target)
            ) {
                setUserMenuOpen(false);
            }
        }

        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, []);


    useEffect(() => {
        if (!changePasswordModal) return;

        const fetchCriteria = async () => {
            try {
                setLoadingCriteria(true);

                const res = await privateApi.get("/cr-order-manager/v1/user/passwrod/criteria");

                setCriteriaList(res.data.criteriaList || []);
            } catch (err) {
                if (axios.isAxiosError(err)) {
                    const message =
                        err.response?.data?.message ||
                        "Errore lato server";
                    toast.error(message);
                } else {
                    toast.error("Errore non gestito");
                }
            } finally {
                setLoadingCriteria(false);
            }
        };

        fetchCriteria();
    }, [changePasswordModal]);

    const handleLogout = async () => {
        try {
            await privateApi.delete("/cr-order-manager/v1/auth/token", {
                    data: {
                        "refreshToken": localStorage.getItem("refreshToken")
                    }
                }
            );
            toast.success("Logout eseguito con successo");
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        } finally {
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
        }
        navigate("/login");
    }

    return (
        <aside className="fixed top-0 left-0 h-screen w-64 bg-blue-900 text-white flex flex-col py-6">

            {/* Title */}
            <h2 className="text-xl font-bold text-center mb-8">
                Casablanca<br/>Riparazioni
            </h2>

            {/* Scrollable Area */}
            <div className="flex-1 overflow-y-auto py-6">
                <nav className="flex flex-col space-y-2 px-4">

                    <SidebarFirstLevelMenuItem value={"Ordini"} path={"/order"}/>
                    <SidebarFirstLevelMenuItem value={"Clienti"} path={"/customer"}/>
                    <SidebarFirstLevelMenuItem value={"Appuntamenti"} path={"/appointment"}/>

                    {/* INVENTARIO */}
                    <div>
                        <button
                            onClick={() => setInventarioOpen(prev => !prev)}
                            className="w-full flex justify-between items-center px-4 py-2 rounded hover:bg-blue-700"
                        >
                            <span>Inventario</span>
                            <span>{inventarioOpen ? "⏷" : "⏵"}</span>
                        </button>

                        {inventarioOpen && (
                            <div className="flex flex-col mt-1 ml-4 space-y-1">
                                <SidebarDropDownFirstLevelMenuItem value={"Brand"} path={"/brand"}/>
                                <SidebarDropDownFirstLevelMenuItem value={"Dispositivi"} path={"/device"}/>
                                <SidebarDropDownFirstLevelMenuItem value={"Ricambi"} path={"/replacement"}/>
                            </div>
                        )}
                    </div>

                    {/* CONFIGURAZIONI */}
                    <div>
                        <button
                            onClick={() => setConfigOpen(prev => !prev)}
                            className="w-full flex justify-between items-center px-4 py-2 rounded hover:bg-blue-700"
                        >
                            <span>Configurazioni</span>
                            <span>{configOpen ? "⏷" : "⏵"}</span>
                        </button>

                        {configOpen && (
                            <div className="flex flex-col mt-1 ml-4 space-y-1">
                                <SidebarDropDownFirstLevelMenuItem value={"Stati"} path={"/status"}/>
                                <SidebarDropDownFirstLevelMenuItem value={"Lavorazioni"} path={"/workType"}/>
                            </div>
                        )}
                    </div>

                    <SidebarFirstLevelMenuItem value={"Servizi Connessi"} path={"/service"}/>

                </nav>
            </div>

            {/* USER SECTION */}
            <div className="mt-auto px-4 relative">

                {/* USER BUTTON */}
                <button
                    ref={btnRef}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        setUserMenuOpen(prev => !prev);
                    }}
                    className="w-full flex items-center gap-3 p-3 bg-blue-800 rounded-lg hover:bg-blue-700 transition"
                >
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                        👤
                    </div>

                    <div className="text-left">
                        <p className="font-semibold text-sm">{username}</p>
                        {/*<p className="text-blue-200 text-xs">Admin</p>*/}
                    </div>
                </button>

                {/* DROPDOWN */}
                {userMenuOpen && (
                    <div
                        ref={menuRef}
                        className="absolute bottom-20 left-4 w-[calc(100%-2rem)] bg-blue-800 rounded-lg shadow-lg overflow-hidden"
                    >
                        <span className="block px-4 py-3 text-sm hover:bg-blue-700 transition"
                              onClick={() => setChangePasswordModal(true)}>
                            Cambia password
                        </span>
                        <span className="block px-4 py-3 text-sm hover:bg-red-600 hover:text-white transition"
                              onClick={handleLogout}>
                            Logout
                        </span>
                    </div>
                )}

                {changePasswordModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white text-black rounded-xl p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4">Cambia Password</h2>

                            <div className="space-y-3">
                                <div className="relative">
                                    <input
                                        className="w-full border p-2 rounded pr-12"
                                        placeholder="Vecchia Password"
                                        type={showPasswordsActual ? "text" : "password"}
                                        value={oldPassword}
                                        onChange={(e) => setOldPassword(e.target.value)}
                                    />

                                    <button
                                        type="button"
                                        onClick={() => setShowPasswordsActual(prev => !prev)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 hover:text-black"
                                    >
                                        {showPasswordsActual ? "🙈" : "👁️"}
                                    </button>
                                </div>

                                <div className="relative">
                                    <input
                                        className="w-full border p-2 rounded pr-12"
                                        placeholder="Nuova password"
                                        type={showPasswordsNew ? "text" : "password"}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                    />

                                    <button
                                        type="button"
                                        onClick={() => setShowPasswordsNew(prev => !prev)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 hover:text-black"
                                    >
                                        {showPasswordsNew ? "🙈" : "👁️"}
                                    </button>
                                </div>

                                {/* PASSWORD CRITERIA */}
                                <div className="bg-gray-50 border rounded p-3 text-sm">

                                    <p className="font-medium mb-2">
                                        Requisiti password
                                    </p>

                                    {loadingCriteria ? (
                                        <p className="text-gray-500">
                                            Caricamento...
                                        </p>
                                    ) : (
                                        <div className="space-y-1">

                                            {criteriaList.map((criteria, index) => {
                                                const valid = validateCriteria(criteria.regex);

                                                return (
                                                    <div
                                                        key={index}
                                                        className={`flex items-center gap-2 ${
                                                            valid
                                                                ? "text-green-600"
                                                                : "text-red-500"
                                                        }`}>
                                                        <span>{valid ? "✓" : "✗"}</span><span>{criteria.description}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end gap-2 mt-4">
                                    <button
                                        onClick={() => {
                                            setChangePasswordModal(false);
                                            resetChangePasswordModal();
                                        }}
                                        className="px-4 py-2 bg-gray-200 rounded"
                                    >
                                        Annulla
                                    </button>

                                    <button
                                        onClick={handleSubmit}
                                        className="px-4 py-2 bg-blue-900 text-white rounded"
                                    >
                                        Salva
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </aside>
    );
}

export default Sidebar;